import React, { useEffect, useState } from 'react';
import { Theme as AugmentedTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { useLocation } from 'react-router-dom';
import {
  Button,
  ContentTemplate,
  KeyValue,
  SectionHeader,
  TitleHeader,
  Typography,
} from 'web-library';
import { fallbackEmptyString, parseLocationSearch } from '../../../utils/StringUtils';
import { getENetsErrorMessages } from '../logic/errorMessageGenerator';

const useStyles = makeStyles()((theme) =>
({
  lastline: {
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  singleLine: {
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
}));

const CloseWindowButton = (props: { btnText: string }) => {
  const { btnText = 'Close this window' } = props;

  return (
    <Button id="button-paymentPopupResult-close" onClick={() => window.close()}>
      {btnText}
    </Button>
  );
};

const MessageContent = ({
  localPostMessageData,
}: {
  localPostMessageData: ENetsPostMessageData;
}) => {
  const { classes } = useStyles();

  return (
    <>
      {localPostMessageData.isSuccess ? (
        <>
          <TitleHeader title="Your payment has been successful." state="success" />
          <ContentTemplate>
            <SectionHeader title="Payment Successful" />
            <KeyValue keyText="CP Reference No." valueText={localPostMessageData.merchantTxnRef} />
            <KeyValue keyText="Transaction No." valueText={localPostMessageData.netsTxnRef} />

            <Typography className={classes.lastline}>
              Click here to view your transaction details.
            </Typography>

            <CloseWindowButton btnText="View Transaction Details" />
          </ContentTemplate>
        </>
      ) : (
        <>
          <TitleHeader title="Your payment has been unsuccessful." state="error" />
          <ContentTemplate>
            <SectionHeader title="Payment Unsuccessful" />

            {getENetsErrorMessages({
              netsStageRespCode: localPostMessageData.netsStageRespCode,
              netsTxnMsg: localPostMessageData.netsTxnMsg,
              isRetry: localPostMessageData.isRetry,
              merchantTxnRef: localPostMessageData.merchantTxnRef,
              netsTxnRef: localPostMessageData.netsTxnRef,
            }).map((errorMessage) => {
              const errorMeta = errorMessage.split(': ');
              if (errorMeta.length !== 2) {
                return (
                  <Typography key={errorMessage} className={classes.singleLine}>
                    {errorMessage}
                  </Typography>
                );
              }
              return (
                <KeyValue key={errorMessage} keyText={errorMeta[0]} valueText={errorMeta[1]} />
              );
            })}
            {localPostMessageData.isRetry && (
              <Typography className={classes.lastline}>
                Please select the payment mode for another payment attempt. Alternatively, you may
                contact SEAB for advice.
              </Typography>
            )}

            {!localPostMessageData.isRetry && (
              <Typography className={classes.lastline}>
                Payment status currently pending verification at eNETS. Please contact SEAB or view
                your application status after 15min. An email will be sent to you within 15min on
                your application status.
              </Typography>
            )}

            <CloseWindowButton btnText="Close this Window" />
          </ContentTemplate>
        </>
      )}
    </>
  );
};

export interface ENetsPostMessageData {
  ENetsPostMessage: 'ENetsPostMessage';
  isSuccess: boolean;
  isRetry: boolean;
  netsStageRespCode?: string;
  netsTxnMsg?: string;
  merchantTxnRef?: string;
  netsTxnRef?: string;
}

const ENetsPaymentInterimResult = () => {
  const location = useLocation();
  const [hasPostedMessage, setHasPostedMessage] = useState(false);
  const [hasWindowOpener, setHasWindowOpener] = useState<boolean | null>(null);

  const [localPostMessageData, setLocalPostMessageData] = useState<ENetsPostMessageData | null>(
    null
  );

  useEffect(() => {
    window.focus();
    const {
      isSuccess,
      isRetry,
      netsStageRespCode,
      netsTxnMsg,
      merchantTxnRef,
      netsTxnRef,
    } = parseLocationSearch(location.search);

    // Backend should redirect to a URL with query parameters that are string only.
    // If the parameter does not exist, then undefined.
    const postMessageData: ENetsPostMessageData = {
      ENetsPostMessage: 'ENetsPostMessage',
      isSuccess: isSuccess === 'true',
      isRetry: isRetry === 'true',
      netsStageRespCode,
      netsTxnMsg,
      merchantTxnRef,
      netsTxnRef,
    };

    setLocalPostMessageData(postMessageData);

    const windowOpener = window.opener;

    if (windowOpener) {
      // windowOpener.location.origin is for IE purpose.
      const urlOrigin = windowOpener.origin || windowOpener.location.origin;
      setHasWindowOpener(true);
      if (!hasPostedMessage) {
        setHasPostedMessage(true);
        setLocalPostMessageData(postMessageData);
        windowOpener.postMessage(postMessageData, fallbackEmptyString(urlOrigin));
      }
    } else {
      setHasWindowOpener(false);
    }
  }, [location, hasPostedMessage]);

  if (hasWindowOpener === null) {
    return null;
  }

  if (localPostMessageData === null) {
    return (
      <ContentTemplate>
        <SectionHeader title="Payment Error" />
        <CloseWindowButton btnText="Close this Window" />
      </ContentTemplate>
    );
  }

  let tempContent = null;
  if (localPostMessageData !== null) {
    tempContent = <MessageContent localPostMessageData={localPostMessageData} />;
  }

  return tempContent;
};

export default ENetsPaymentInterimResult;
